import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { from, of, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface OpcionesUrlImagen {
  /** Tipos MIME aceptados (`image/png`, ...). */
  tipos: string[];
  minLadoPx?: number;
  maxBytes?: number;
  /** Lo que no se pudo verificar (no invalida el campo, solo se avisa). null = nada que avisar. */
  onAviso?: (aviso: string | null) => void;
}

/** Extensión → tipo, para rechazar una URL que dice ser otra cosa. Sin extensión no se juzga. */
const TIPO_POR_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
};

/**
 * Valida una URL de imagen pegada a mano, con las mismas reglas que se aplican al subir un archivo.
 *
 * Desde el navegador no todo se puede verificar:
 * - Que cargue y sus medidas: sí, cargándola en un `<img>` (no necesita CORS).
 * - Tipo y peso: solo si el servidor responde el HEAD con CORS. El bucket no lo hace para
 *   cualquier origen, así que si falla se avisa por [OpcionesUrlImagen.onAviso] sin invalidar.
 * - La extensión solo sirve para rechazar: las URLs que genera la subida no tienen extensión.
 *
 * Espera medio segundo desde el último cambio antes de cargar nada (Angular cancela la
 * validación anterior en cada tecla).
 */
export function validadorUrlImagen(op: OpcionesUrlImagen): AsyncValidatorFn {
  return (control: AbstractControl) => {
    const url = String(control.value ?? '').trim();
    if (!url) {
      op.onAviso?.(null);
      return of(null);
    }
    return timer(500).pipe(switchMap(() => from(validar(url, op))));
  };
}

async function validar(
  url: string,
  op: OpcionesUrlImagen,
): Promise<ValidationErrors | null> {
  const permitidos = op.tipos
    .map((t) => t.replace('image/', '').toUpperCase())
    .join(', ');
  op.onAviso?.(null);

  const ext = extension(url);
  const tipoExt = ext ? TIPO_POR_EXTENSION[ext] : undefined;
  if (ext && tipoExt && !op.tipos.includes(tipoExt)) {
    return {
      urlImagen: `La URL es ${ext.toUpperCase()}: tiene que ser ${permitidos}`,
    };
  }

  let medidas: { width: number; height: number };
  try {
    medidas = await medidasDe(url);
  } catch {
    return { urlImagen: 'No se pudo cargar una imagen desde esa URL' };
  }
  const min = op.minLadoPx;
  if (min && (medidas.width < min || medidas.height < min)) {
    return {
      urlImagen: `La imagen mide ${medidas.width} × ${medidas.height} px: tiene que tener al menos ${min} × ${min} px`,
    };
  }

  const cabecera = await cabeceraDe(url);
  if (!cabecera) {
    const peso = op.maxBytes ? ` de hasta ${mb(op.maxBytes)}` : '';
    op.onAviso?.(
      `No se pudo verificar el formato ni el peso desde el navegador: asegurate de que sea ${permitidos}${peso}`,
    );
    return null;
  }
  if (cabecera.tipo && !op.tipos.includes(cabecera.tipo)) {
    return {
      urlImagen: `El archivo es ${cabecera.tipo}: tiene que ser ${permitidos}`,
    };
  }
  if (op.maxBytes && cabecera.bytes && cabecera.bytes > op.maxBytes) {
    return { urlImagen: `La imagen pesa más de ${mb(op.maxBytes)}` };
  }
  return null;
}

function extension(url: string): string | undefined {
  try {
    const ultimo = new URL(url).pathname.split('/').pop() ?? '';
    const punto = ultimo.lastIndexOf('.');
    return punto > 0 ? ultimo.slice(punto + 1).toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}

function medidasDe(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const corte = setTimeout(() => reject(new Error('timeout')), 10_000);
    img.onload = () => {
      clearTimeout(corte);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      clearTimeout(corte);
      reject(new Error('error'));
    };
    img.src = url;
  });
}

/** Tipo y peso por HEAD. null si el servidor no lo permite (CORS) o no responde. */
async function cabeceraDe(
  url: string,
): Promise<{ tipo?: string; bytes?: number } | null> {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    if (!res.ok) return null;
    const tipo =
      res.headers.get('content-type')?.split(';')[0].trim() || undefined;
    const largo = Number(res.headers.get('content-length'));
    return { tipo, bytes: largo > 0 ? largo : undefined };
  } catch {
    return null;
  }
}

function mb(bytes: number): string {
  return `${(bytes / 1_000_000).toLocaleString('es-AR')} MB`;
}
